// Phase 5 (web revival) — auth store.
//
// Access token lives in module memory only. The refresh token lives in
// the HttpOnly `dvphoto_refresh` cookie set by the server on
// `POST /v1/auth/password` and rotated on `POST /v1/auth/refresh`.
// `fetch(..., { credentials: 'include' })` ships the cookie back to the
// refresh endpoint automatically; the SPA never sees its value.
//
// Subscribers (router, WS client, query client) get notified via a tiny
// listener set so a logout can rip the rug out from under everything
// without a global state lib.

import type { AuthEnvelope, UserDTO } from '../api/types';

type Listener = () => void;

interface AuthState {
  accessToken: string | null;
  user: UserDTO | null;
  /// Expiry unix-ms of the current access token. We treat anything within
  /// 30s of expiry as already-stale so the next request triggers a refresh
  /// instead of racing the server clock.
  accessExpiresAt: number;
}

const state: AuthState = {
  accessToken: null,
  user: null,
  accessExpiresAt: 0,
};

const listeners = new Set<Listener>();

function emit() {
  for (const l of listeners) l();
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function snapshot(): Readonly<AuthState> {
  return state;
}

export function getAccessToken(): string | null {
  return state.accessToken;
}

export function isAuthenticated(): boolean {
  return state.accessToken !== null && state.user !== null;
}

export function setSession(env: AuthEnvelope) {
  state.accessToken = env.access_token;
  state.user = env.user;
  // Server returns expires_in in seconds; convert to absolute ms with a
  // 30-second safety margin.
  state.accessExpiresAt = Date.now() + env.expires_in * 1000 - 30_000;
  emit();
}

export function clearSession() {
  state.accessToken = null;
  state.user = null;
  state.accessExpiresAt = 0;
  emit();
}

export function isAccessTokenFresh(): boolean {
  return state.accessToken !== null && Date.now() < state.accessExpiresAt;
}
